import React from 'react';
import { ReportSection, ReportSubsection } from '../framework';
import { CalloutBox, StatCard, DataTable } from '../primitives';
import { SectionProps } from './types';
import { Citation } from '../Citation';

const comparisonData = [
  { type: 'Conservative (broad markets)', re: '2.3%', stocks: '7%', logic: 'National RE vs S&P 500' },
  { type: 'Moderate (regional)', re: '3.5%', stocks: '7%', logic: 'LA/California vs S&P 500' },
  { type: '* Asymmetric (cherry-picked RE)', re: '8-10%', stocks: '7%', logic: 'Newport Beach vs S&P 500', highlighted: true },
  { type: 'Aggressive (both cherry-picked)', re: '8-10%', stocks: '12-15%', logic: 'Newport Beach vs NASDAQ' },
];

export const GeographySection: React.FC<Pick<SectionProps, 'citations'>> = ({ citations }) => {
  return (
    <ReportSection id="geography" title="Real Estate Returns by Geography" subtitle="From national averages to your neighborhood">
      <p className="text-gray-700 mb-6">
        Now let's look at what real estate has actually returned historically. Spoiler: it's less than most
        people think, but Newport Beach is unusual.
      </p>

      <ReportSubsection title="National Returns: The Baseline">
        <p className="text-gray-700 mb-4">
          According to the <Citation citation={citations.caseShillerNational}>Case-Shiller National Home Price Index</Citation>,
          US home prices have appreciated at roughly <span className="font-semibold text-gray-900 border-b border-dotted border-gray-400">2.3% per year after inflation</span> over the past 35+ years.
        </p>

        <p className="text-gray-700 mb-4">
          That number surprises many people. "But my parents' house went up 5x!" Yes, but inflation also went
          up 3-4x over that same period. In real purchasing power terms, most of the country's housing has been
          a modest-returning asset—more like bonds than stocks.
        </p>

        <p className="text-gray-700 mb-4">
          This 2.3% doesn't include rental income. If you owned a rental property generating 4-5% net yield,
          your total return would be higher. But for most homeowners who live in their house, the appreciation
          alone is the bulk of their return (minus mortgage interest, property taxes, maintenance...).
        </p>
      </ReportSubsection>

      <ReportSubsection title="California's Outperformance">
        <p className="text-gray-700 mb-4">
          California, and especially coastal California, has substantially outperformed national averages. The
          <Citation citation={citations.caseShillerLA}>Case-Shiller LA Index</Citation> shows about <span className="font-semibold text-gray-900 border-b border-dotted border-gray-400">3.5%
          real appreciation</span> over the past 25 years—50% higher than national.
        </p>

        <p className="text-gray-700 mb-4">
          Why? A combination of factors:
        </p>

        <ul className="list-disc list-inside text-gray-600 space-y-1 mb-4">
          <li><strong>Supply constraints:</strong> CEQA regulations, local opposition, and geography limit new construction</li>
          <li><strong>Prop 13:</strong> Property tax limits encourage people to hold properties longer</li>
          <li><strong>Desirability:</strong> Weather, jobs, culture attract high-income buyers</li>
          <li><strong>Tech wealth:</strong> Especially in the past decade, Bay Area and LA tech money has flooded in</li>
        </ul>

        <p className="text-gray-700">
          As of late 2024, the LA Case-Shiller index stands at about 440 (where January 2000 = 100), compared
          to about 320 for the national index. LA has simply grown faster.
        </p>
      </ReportSubsection>

      <ReportSubsection title="Newport Beach: The Premium Market">
        <p className="text-gray-700 mb-4">
          Newport Beach is even more extreme than LA. This is one of the most expensive markets in the country,
          and recent appreciation has been remarkable:
        </p>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <StatCard label="Median Price (2019)" value="~$1.9M" sublabel="Pre-COVID baseline (Zillow)" />
          <StatCard label="Median Price (2024)" value="~$3.5M+" sublabel="Per Redfin/Zillow estimates" />
        </div>

        <p className="text-gray-700 mb-4">
          That's roughly <strong>80-100% appreciation in 5 years</strong>, or about 12-15% per year. Even
          adjusting for inflation (~20% cumulative over this period), real returns have been extraordinary—
          probably in the 8-10% per year range.
        </p>

        <CalloutBox type="insight" title="Can This Continue?">
          <p className="mb-2">
            This is the trillion-dollar question. Newport Beach appreciation has been driven by:
          </p>
          <ul className="list-disc list-inside space-y-1">
            <li>COVID-era migration to coastal areas and larger homes</li>
            <li>Low interest rates making expensive homes more affordable</li>
            <li>Wealthy buyers competing for limited inventory</li>
            <li>Prop 13 discouraging existing owners from selling</li>
          </ul>
          <p className="mt-2">
            Some of these factors are structural (supply constraints). Others were temporary (COVID migration,
            ultra-low rates). Future returns are unlikely to match the 2020-2024 period, but Newport Beach's
            fundamental scarcity remains.
          </p>
        </CalloutBox>
      </ReportSubsection>

      <ReportSubsection title="The Selection Bias Problem">
        <p className="text-gray-700 mb-4">
          Here's something important to consider: if we use Newport Beach's exceptional recent returns to project
          forward, we're cherry-picking the single best-performing geographic subset of real estate. But when we
          model the stock alternative, we typically use the broad S&P 500—not the best-performing subset (tech stocks).
        </p>

        <p className="text-gray-700 mb-4">
          This creates an intellectual inconsistency. Look at how different the comparison looks depending on
          which assumptions you pair together:
        </p>

        <DataTable
          columns={[
            { key: 'type', header: 'Comparison Type', align: 'left' },
            { key: 're', header: 'Real Estate', align: 'right' },
            { key: 'stocks', header: 'Stocks', align: 'right' },
            { key: 'logic', header: 'Logic', align: 'left', muted: true }
          ]}
          data={comparisonData}
          highlightRow={(row) => row.highlighted === true}
        />

        <p className="text-gray-700 mt-4 mb-4">
          The "Asymmetric" row is what many people implicitly assume: Newport Beach will keep crushing it,
          but stocks will just return their historical average. That's not necessarily wrong—maybe Newport Beach
          really is special—but it's worth recognizing that you're making an asymmetric bet.
        </p>

        <CalloutBox type="warning" title="The Consistency Test">
          <p className="mb-2">
            Ask yourself: If you're bullish enough on Newport Beach to assume it keeps beating national real estate
            by 4-6 percentage points annually, shouldn't you also consider whether tech stocks might keep beating
            the S&P 500?
          </p>
          <p>
            Either believe in mean reversion for both (use conservative assumptions), or believe recent outperformers
            will keep outperforming for both. The inconsistent middle—where only <em>your</em> asset keeps winning—is
            often wishful thinking.
          </p>
        </CalloutBox>

        <p className="text-gray-700 mt-4">
          For what it's worth, the "balanced" scenario in this analysis uses LA/California returns (~3.5%) rather than
          Newport Beach's recent exceptional performance. That's a defensible middle ground—acknowledging California's
          structural advantages without assuming the COVID-era boom continues forever.
        </p>
      </ReportSubsection>
    </ReportSection>
  );
};
